/**
 * SpotifyImportDialog — Importa una playlist de Spotify como peticiones del
 * evento, todas etiquetadas con el `momento` por defecto del bucket activo.
 *
 * NO reproduce nada dentro de la app (regla del usuario): sólo guarda las
 * canciones como peticiones con `spotify_url` para que aparezcan en el pad
 * (con botón Buscar/Abrir en Spotify) y sean ranqueables por el DJ.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, ExternalLink, Music } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config';
import { BUCKET_LABELS } from '../utils/momentoBuckets';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const isValidSpotifyPlaylistUrl = (url) => {
  if (!url) return false;
  return /spotify\.com\/playlist\/[A-Za-z0-9]+/.test(url) || /^spotify:playlist:[A-Za-z0-9]+/.test(url);
};

export const SpotifyImportDialog = ({
  open, onOpenChange, playlistId, bucket, onImported,
}) => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);

  const handleImport = async () => {
    if (!isValidSpotifyPlaylistUrl(url.trim())) {
      toast.error('Introduce una URL válida de playlist de Spotify (ej. https://open.spotify.com/playlist/…)');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/playlists/${playlistId}/peticiones-import-spotify`,
        { playlist_url: url.trim(), bucket },
        { headers: authHeaders(), timeout: 60000 },
      );
      const { created = 0, skipped = 0, playlist_name = '' } = res.data || {};
      if (created > 0) {
        toast.success(
          `${created} canciones importadas${playlist_name ? ` de "${playlist_name}"` : ''} → ${BUCKET_LABELS[bucket]}${skipped > 0 ? ` (${skipped} duplicadas omitidas)` : ''}`,
          { duration: 6000 },
        );
      } else if (skipped > 0) {
        toast.message(`Nada nuevo: las ${skipped} canciones ya estaban como peticiones`);
      } else {
        toast.warning('La playlist estaba vacía o no se pudo leer');
      }
      setUrl('');
      if (typeof onImported === 'function') onImported(res.data);
      onOpenChange(false);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Error importando desde Spotify';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700" data-testid="spotify-import-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Music className="w-5 h-5 text-emerald-400" />
            Importar desde Spotify — {BUCKET_LABELS[bucket] || bucket}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Pega la URL de una playlist de Spotify. Cada canción se creará como
            petición con enlace de Spotify. La app <strong>no reproduce</strong>{' '}
            los tracks directamente — se abren en Spotify al pulsar el pad.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <Input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://open.spotify.com/playlist/…"
            className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
            disabled={loading}
            autoFocus
            data-testid="spotify-import-url-input"
          />
          <p className="text-xs text-slate-500">
            Las canciones se etiquetarán como{' '}
            <span className="text-amber-300 font-semibold">{BUCKET_LABELS[bucket]}</span>{' '}
            y aparecerán bajo esa subpestaña.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="bg-slate-800 border-slate-600 text-slate-300 hover:bg-slate-700"
            data-testid="spotify-import-cancel"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleImport}
            disabled={loading || !url.trim()}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="spotify-import-confirm"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Importando…
              </>
            ) : (
              <>
                <ExternalLink className="w-4 h-4 mr-1" />
                Importar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SpotifyImportDialog;
